import type { ChangeEvent, FC } from 'react';
import { useCallback, useState, useRef, useEffect, memo } from 'react';
import ChevronDownIcon from '@untitled-ui/icons-react/build/esm/ChevronDown';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import SvgIcon from '@mui/material/SvgIcon';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import { usePopover } from 'src/hooks/use-popover';
import { Search } from '@mui/icons-material';
import { Box } from '@mui/system';
import _ from 'lodash';
import Typography from '@mui/material/Typography';

interface MultiSelectWithSearchProps {
  label: string;
  onChange?: (value: any[]) => void;
  options: { label: string; value: unknown }[];
  value: any[];
  isMulti?: boolean;
  selectAll?: boolean;
  selectAllLabel?: string;
  withSearch?: boolean;
  searchString?: string;
  setSearchString?: (value: string) => void;
  onScroll?: (page?: number) => void;
  loading?: boolean;
  shouldRunApiCall?: boolean;
  isAllSelected?: boolean;
  setIsAllSelected?: (value: boolean) => void;
  setSelectedValuesString?: (value: string) => void;
  resetValues?: boolean;
  noResultsText?: string;
  onClose?: () => void;
  sx?: Record<string, unknown>;
}

export const MultiSelectWithSearch: FC<MultiSelectWithSearchProps> = memo((props) => {
  const {
    label,
    onChange,
    options,
    value,
    isMulti = [],
    selectAll = false,
    selectAllLabel,
    withSearch,
    searchString = '',
    setSearchString = () => {},
    onScroll = () => {},
    loading,
    shouldRunApiCall,
    isAllSelected = false,
    setIsAllSelected = () => {},
    setSelectedValuesString = () => {},
    resetValues = false,
    noResultsText,
    onClose,
    ...other
  } = props;

  const popover = usePopover<HTMLButtonElement>();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [menuValue, setMenuValue] = useState(value);

  const handleValueChange = useCallback(
    (
      event: ChangeEvent<HTMLInputElement> | { target: { checked: boolean; value: string } }
    ): void => {
      let newValue = isMulti ? [...menuValue] : [];

      const eventValue = event.target.value;

      if (event.target.checked) {
        if (!newValue.includes(eventValue)) {
          newValue.push(eventValue);
          if (options.length === newValue.length && searchString.length === 0) {
            setIsAllSelected(true);
          }
        }
      } else {
        newValue = newValue.filter((item) => item !== eventValue);
        setIsAllSelected(false);
      }

      onChange?.(newValue);
      setMenuValue(newValue);

      if (!isMulti) {
        popover.handleClose();
      }
    },
    [onChange, setIsAllSelected, menuValue, options, isMulti, popover, searchString.length]
  );

  const handleSelectAllChange = useCallback(
    (event: ChangeEvent<HTMLInputElement> | { target: { checked: boolean; value: string } }) => {
      const checked = event.target.checked;
      let newArray = [];

      if (checked) {
        // Select only the currently displayed/filtered options
        newArray = options.map(option => option.value);
      } else {
        // Deselect all currently displayed options
        newArray = menuValue.filter(value => 
          !options.some(option => option.value === value)
        );
      }
      
      onChange?.(newArray);
      setMenuValue(newArray);
      setIsAllSelected(checked);
    },
    [onChange, setIsAllSelected, options, menuValue]
  );

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setSearchString(event.target.value);
  };

  const avoidBubblingUp = (e: React.KeyboardEvent) => {
    e.stopPropagation();
  };

  const handleScroll = () => {
    if (!containerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 10 && !loading) {
      if (shouldRunApiCall) {
        onScroll();
      }
    }
  };

  useEffect(() => {
    if (resetValues) {
      setMenuValue([]);
    }
    return () => {
      setMenuValue([]);
    };
  }, [resetValues]);

  // When "All Patients" is active, keep all currently displayed/filtered options selected
  useEffect(() => {
    if (isAllSelected) {
      const allValues = options.map((option) => option.value);
      setMenuValue(allValues);
    }
  }, [isAllSelected, options]);

  useEffect(() => {
    // set string to be sent to api
    const selectedIds = (menuValue || []).join(',');

    setSelectedValuesString(selectedIds);
    return () => {
      setSelectedValuesString('');
    };
  }, [menuValue, setSelectedValuesString]);

  const AllPatientsMenuItem = () => {
    const selectAllChecked = isAllSelected;
    return (
      <>
        <MenuItem>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectAllChecked}
                onChange={handleSelectAllChange}
              />
            }
            label={selectAllLabel || ''}
            sx={{
              flexGrow: 1,
              mr: 0,
            }}
          />
        </MenuItem>
        <Divider sx={{ margin: '0px 16px' }} />
      </>
    );
  };

  // Get selected patient names for tooltip
  const getSelectedPatientNames = () => {
    if (isAllSelected) {
      return options.map(option => option.label).join(' | ');
    } else {
      const selectedOptions = options.filter(option => menuValue.includes(option.value));
      return selectedOptions.map(option => option.label).join(' | ');
    }
  };

  const tooltipContent = getSelectedPatientNames();

  interface IMultiMenuItemProps {
    option: {
      label: string;
      value: unknown;
    };
  }

  const MultiMenuItem = ({ option }: IMultiMenuItemProps) => {
    return (
      <>
        {isMulti ? (
          <MenuItem
            key={option.label}
            sx={{
              '& .MuiFormControlLabel-root .MuiFormControlLabel-label': {
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                width: '188px',
              },
            }}
            title={option.label}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={menuValue.includes(option.value)}
                  onChange={handleValueChange}
                  value={option.value}
                />
              }
              label={option.label}
              sx={{
                flexGrow: 1,
                mr: 0,
              }}
            />
          </MenuItem>
        ) : (
          <MenuItem
            sx={{
              background: menuValue.includes(option.value) ? 'rgba(99, 102, 241, 0.08)' : '',
            }}
            onClick={() => {
              handleValueChange({
                target: {
                  checked: !menuValue.includes(option.value),
                  value: option.value as string,
                },
              });
            }}
            title={option.label}
          >
            {option.label}
          </MenuItem>
        )}
      </>
    );
  };

  return (
    <>
      <Tooltip
        title={tooltipContent || 'No patients selected'}
        placement="bottom-start"
        arrow
        enterDelay={500}
        leaveDelay={0}
      >
        <Button
          color="inherit"
          endIcon={
            <SvgIcon>
              <ChevronDownIcon />
            </SvgIcon>
          }
          onClick={popover.handleOpen}
          ref={popover.anchorRef}
          disabled={options.length === 0 && value.length === 0}
          {...other}
        >
          <div
            style={{
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            {label}
          </div>
        </Button>
      </Tooltip>
      <Menu
        anchorEl={popover.anchorRef.current}
        onClose={() => {
          popover.handleClose();
          onClose?.();
        }}
        open={popover.open}
        slotProps={{
          paper: { style: { width: 250, height: withSearch ? 500 : 'auto' } },
        }}
      >
        {withSearch ? (
          <TextField
            variant="outlined"
            placeholder={`Search by Patient`}
            fullWidth
            onChange={handleSearchInputChange}
            value={searchString}
            sx={{
              p: 1,
              pt: 0,
              '& .MuiInputBase-input': {
                padding: '12px 0px',
              },
            }}
            inputRef={inputRef}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: loading ? (
                <InputAdornment position="end">
                  <CircularProgress size={16} />
                </InputAdornment>
              ) : undefined,
            }}
            onKeyDown={avoidBubblingUp}
          />
        ) : null}

        <Box
          ref={containerRef}
          onScroll={handleScroll}
          style={{
            height: '420px',
            overflowY: 'auto',
          }}
        >
          {selectAll && !loading && options.length > 1 ? <AllPatientsMenuItem /> : null}
          {/* Show no results message when no options available */}
          {!loading && options.length === 0 && noResultsText && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              p={3}
              sx={{ minHeight: '100px' }}
            >
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary' }}
              >
                {noResultsText}
              </Typography>
            </Box>
          )}
          {options?.map((option, index) => (
            <MultiMenuItem
              key={`${index}-${option.value}`}
              option={option}
            />
          ))}

          {loading && (
            <Box
              display="flex"
              justifyContent="center"
              p={2}
            >
              <CircularProgress />
            </Box>
          )}
        </Box>
      </Menu>
    </>
  );
});

MultiSelectWithSearch.displayName = 'MultiSelectWithSearch';